import { ReactNode, useState } from 'react'
import { useFilesState } from '../../context/FilesContext'
import Modal from './Modal'
import { PrimaryButton, SecondaryButton } from '../ui/Button'
import { toast } from 'react-toastify'
import { stringifyError } from '../../utils/json'
import { PopupOnClickEventHander } from '../../types'

type Props = {
  header: ReactNode
  content: ReactNode
  onConfirmClick: PopupOnClickEventHander
  onCancelClick?: PopupOnClickEventHander
}

const ConfirmationModal = ({
  content,
  header,
  onConfirmClick,
  onCancelClick,
}: Props) => {
  const [loading, setLoading] = useState(false)
  const { state, dispatch } = useFilesState()
  const openPopup = state.openPopup

  const close = () =>
    dispatch({
      type: 'SET_OPEN_POPUP',
      payload: false,
    })

  const handleConfirmClick = async () => {
    try {
      setLoading(true)
      await onConfirmClick({
        event: {} as React.MouseEvent<HTMLElement, MouseEvent>,
        closePopup: () => {},
        setSpinner: setLoading,
      })
    } catch (error) {
      toast.error(`Confirmation Modal Error: ${stringifyError(error)}`)
    } finally {
      setLoading(false)
      close()
    }
  }

  const handleCancelClick = () => {
    if (onCancelClick) {
      onCancelClick({
        event: {} as React.MouseEvent<HTMLElement, MouseEvent>,
        closePopup: close,
        setSpinner: setLoading,
      })
    } else {
      close()
    }
  }

  return (
    <div className="w-72 flex">
      <Modal
        isOpen={openPopup}
        setIsOpen={(val: boolean) => {
          if (!loading) {
            dispatch({
              type: 'SET_OPEN_POPUP',
              payload: val,
            })
          }
        }}
        size="sm"
      >
        <Modal.Header onClose={loading ? () => {} : close}>
          {header}
        </Modal.Header>
        <Modal.Separator />
        <Modal.Body>
          <div
            className={`${
              loading ? 'pointer-events-none opacity-75' : ''
            } transition-opacity duration-200`}
          >
            {content}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <PrimaryButton
            disabled={loading}
            onClick={handleConfirmClick}
            spinner={loading}
          >
            Confirm
          </PrimaryButton>
          <SecondaryButton disabled={loading} onClick={handleCancelClick}>
            Cancel
          </SecondaryButton>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default ConfirmationModal
